import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CreateButton from './CreateButton';
import PageNavigator from './PageNavigator';
import CustomerProfile from './CustomerProfile';
import AddCustomer from './AddCustomer';
import FilterDialog from './FilterDialog';
import { fetchCustomers, fetchSettledCustomers, fetchDebitCustomers, exportCustomers } from '../services/customerService';
import { searchProjects } from '../services/projectService';
import { showSuccessNotification, showErrorNotification } from '../store/slices/notificationSlice';
import { useCachedPermissions } from '../hooks/useCachedPermissions';
import styles from './CustomersTabContent.module.css';

const CUSTOMERS_PER_PAGE = 15;

const CustomersTabContent = ({ selectedTab }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Customer permissions
    const { hasAddCustomer, hasExportCustomer, hasShowGroup, permissionsLoading } = useCachedPermissions();

    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedCustomerType, setSelectedCustomerType] = useState('all');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showAddCustomer, setShowAddCustomer] = useState(false);
    const [exportLoading, setExportLoading] = useState(false);
    const [showFilterDialog, setShowFilterDialog] = useState(false);
    const [filterValues, setFilterValues] = useState({});
    const [projectSearchResults, setProjectSearchResults] = useState([]);
    const [projectSearchLoading, setProjectSearchLoading] = useState(false);

    // Add pagination state for server-side pagination
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        limit: CUSTOMERS_PER_PAGE,
        results: 0,
        last_page: 1
    });

    // Fetch customers when component mounts
    useEffect(() => {
        fetchCustomersData({}, null, 1);
    }, []); // Only run on mount

    const fetchCustomersData = async (filters = {}, customerType = null, page = null) => {
        setLoading(true);
        setError(null);
        try {
            let response;
            // Use passed parameters or fall back to state values
            const typeToUse = customerType || selectedCustomerType;
            const pageToUse = page || currentPage;

            // Create pagination parameters
            const paginationParams = {
                page: pageToUse,
                limit: CUSTOMERS_PER_PAGE
            };

            // Merge filters with pagination parameters
            const requestParams = {
                ...filters,
                ...paginationParams
            };

            if (typeToUse === 'settler') {
                response = await fetchSettledCustomers(requestParams);
                console.log('Settled customers response:', response);
            } else if (typeToUse === 'debtor') {
                response = await fetchDebitCustomers(requestParams);
                console.log('Debit customers response:', response);
            } else {
                response = await fetchCustomers(requestParams);
                console.log('All customers response:', response);
            }

            if (response.data && response.data.data) {
                setCustomers(response.data.data);

                // Update pagination info from server response
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                } else {
                    // Fallback if pagination info is not in the expected format
                    setPagination({
                        total: response.data.total || response.data.data.length,
                        page: pageToUse,
                        limit: CUSTOMERS_PER_PAGE,
                        results: response.data.data.length,
                        last_page: Math.ceil((response.data.total || response.data.data.length) / CUSTOMERS_PER_PAGE)
                    });
                }
            } else {
                setCustomers([]);
                setPagination({
                    total: 0,
                    page: pageToUse,
                    limit: CUSTOMERS_PER_PAGE,
                    results: 0,
                    last_page: 1
                });
            }
        } catch (err) {
            console.error('Error fetching customers:', err);
            setError(err.message);
            setCustomers([]);
            setPagination({
                total: 0,
                page: currentPage,
                limit: CUSTOMERS_PER_PAGE,
                results: 0,
                last_page: 1
            });
        } finally {
            setLoading(false);
        }
    };

    const getFilteredCustomers = () => {
        // Since pagination is now handled server-side, just return customers
        return customers;
    };

    const filteredCustomers = getFilteredCustomers();
    // Remove client-side pagination calculations since server handles it now
    const paginatedCustomers = filteredCustomers;

    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.last_page) {
            setCurrentPage(page);
            // Fetch data for the new page
            fetchCustomersData(filterValues, selectedCustomerType, page);
        }
    };

    const handleCustomerTypeChange = (type) => {
        setSelectedCustomerType(type);
        setCurrentPage(1);
        setCustomers([]); // Clear customers immediately to prevent showing old data
        // When changing customer type, pass the new type and reset to page 1
        fetchCustomersData(filterValues, type, 1);
    };

    const formatCurrency = (amount) => {
        if (!amount) return 'N/A';
        return new Intl.NumberFormat('en-EG', {
            style: 'currency',
            currency: 'EGP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return dateString; // API already provides formatted date
    };

    const handleFilter = () => {
        console.log('Filter button clicked - opening dialog');
        setShowFilterDialog(true);
    };

    const handleFilterApply = (newFilterValues) => {
        console.log('Filter applied:', newFilterValues);
        setFilterValues(newFilterValues);
        setCurrentPage(1); // Reset to first page when applying filters

        // Fetch customers with the new filter values and reset to page 1
        fetchCustomersData(newFilterValues, selectedCustomerType, 1);
    };

    const handleFilterClose = () => {
        setShowFilterDialog(false);
    };

    const handleResetFilter = () => {
        console.log('Reset filter clicked - making API request to clear filters');
        setFilterValues({});
        setCurrentPage(1);
        setProjectSearchResults([]); // Clear search results

        // Always make API request to fetch unfiltered data and reset to page 1
        fetchCustomersData({}, selectedCustomerType, 1);
    };

    // Debounce function for search
    const debounce = (func, delay) => {
        let timeoutId;
        return (...args) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => func.apply(null, args), delay);
        };
    };

    // Project search function
    const searchProjectsFunction = async (searchTerm) => {
        // Don't search if term is empty or too short
        if (!searchTerm || searchTerm.trim().length < 2) {
            setProjectSearchResults([]);
            setProjectSearchLoading(false);
            return;
        }

        setProjectSearchLoading(true);
        try {
            const response = await searchProjects(searchTerm.trim());
            console.log('Search response:', response);
            if (response.data && Array.isArray(response.data)) {
                setProjectSearchResults(response.data);
            } else {
                setProjectSearchResults([]);
            }
        } catch (error) {
            console.error('Error searching projects:', error);
            setProjectSearchResults([]);
        } finally {
            setProjectSearchLoading(false);
        }
    };

    // Debounced search function
    const debouncedProjectSearch = debounce(searchProjectsFunction, 300);

    // Define basic filter fields for now
    const filterFields = [
        {
            type: 'searchable-select',
            name: 'projectName',
            label: 'Project Name',
            placeholder: 'Search for a project...',
            onSearch: debouncedProjectSearch,
            searchOptions: projectSearchResults,
            loading: projectSearchLoading,
            displayKey: 'name',
            valueKey: '_id'
        },
        {
            type: 'date',
            name: 'Date',
            label: 'Date',
            placeholder: 'Select date'
        },

    ];

    const handleExport = async () => {
        if (!hasExportCustomer) {
            dispatch(showErrorNotification('You do not have permission to export customers'));
            return;
        }

        setExportLoading(true);
        let status = 'all';
        if (selectedCustomerType === 'settler') status = 'settled';
        else if (selectedCustomerType === 'debtor') status = 'depit';
        try {
            await exportCustomers(status);
            dispatch(showSuccessNotification('Customers exported successfully!'));
        } catch (err) {
            dispatch(showErrorNotification(err.message || 'Failed to export customers'));
        } finally {
            setExportLoading(false);
        }
    };

    const handleAddCustomerClick = () => {
        if (!hasAddCustomer) {
            dispatch(showErrorNotification('You do not have permission to add customers'));
            return;
        }
        setShowAddCustomer(true);
    };

    const handleGroupsClick = () => {
        navigate('/customers/groups');
    };

    const handleCustomerClick = (customer) => {
        setSelectedCustomer(customer);
    };

    const handleViewCustomerProfile = (customer, event) => {
        event.stopPropagation(); // Prevent triggering the card click
        navigate(`/customer/${customer._id}`);
    };

    const handleBackToCustomers = () => {
        setSelectedCustomer(null);
    };

    const handleEditCustomer = async (updatedCustomer) => {
        console.log('Customer updated:', updatedCustomer);
        // Refresh the customers list to reflect the updated data, maintain current page
        try {
            await fetchCustomersData(filterValues, selectedCustomerType, currentPage);
        } catch (error) {
            console.error('Failed to refresh customers list after update:', error);
        }
    };

    const handleDeleteCustomer = async (customerId) => {
        console.log('Customer deleted:', customerId);
        // Refresh the customers list to remove the deleted customer, maintain current page
        try {
            await fetchCustomersData(filterValues, selectedCustomerType, currentPage);
        } catch (error) {
            console.error('Failed to refresh customers list after delete:', error);
        }
    };

    const handleCancelAddCustomer = () => {
        setShowAddCustomer(false);
    };

    const handleSaveCustomer = async (customerData) => {
        console.log('New customer data:', customerData);
        // Close the form first
        setShowAddCustomer(false);

        // Refresh the customers list to include the new customer, go back to page 1 to see new customer
        try {
            setCurrentPage(1);
            await fetchCustomersData(filterValues, selectedCustomerType, 1);
        } catch (error) {
            console.error('Failed to refresh customers list:', error);
        }
    };

    // If showing add customer form, display it
    if (showAddCustomer) {
        return (
            <AddCustomer
                onSave={handleSaveCustomer}
                onCancel={handleCancelAddCustomer}
            />
        );
    }

    // If a customer is selected, show the profile
    if (selectedCustomer) {
        return (
            <CustomerProfile
                customerId={selectedCustomer._id}
                onBack={handleBackToCustomers}
                onEdit={handleEditCustomer}
                onDelete={handleDeleteCustomer}
            />
        );
    }

    return (
        <div className={styles.customersContainer}>
            {/* Header */}
            <div className={styles.headerContainer}>
                <div className={styles.headerLeft}>
                    <h2 className={styles.headerTitle}>Customers</h2>
                </div>
                <div className={styles.headerActions}>
                    <button className={styles.actionButton} onClick={handleFilter}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46 22,3" />
                        </svg>
                        Filter
                    </button>
                    <button className={styles.actionButton} onClick={handleResetFilter}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                            <path d="M21 3v5h-5" />
                            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                            <path d="M3 21v-5h5" />
                        </svg>
                        Reset
                    </button>
                    {hasExportCustomer && (
                        <button className={styles.actionButton} onClick={handleExport} disabled={exportLoading}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7,10 12,15 17,10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                            {exportLoading ? 'Exporting...' : 'Export'}
                        </button>
                    )}
                    {hasAddCustomer && (
                        <CreateButton label="+ Add Customer" onClick={handleAddCustomerClick} />
                    )}
                    {hasShowGroup && (
                        <CreateButton label="Groups" onClick={handleGroupsClick} />
                    )}
                </div>
            </div>

            {/* Customer Type Tabs */}
            <div className={styles.tabsContainer}>
                <button
                    className={`${styles.tabButton} ${selectedCustomerType === 'all' ? styles.active : ''}`}
                    onClick={() => handleCustomerTypeChange('all')}
                >
                    All
                </button>
                <button
                    className={`${styles.tabButton} ${selectedCustomerType === 'settler' ? styles.active : ''}`}
                    onClick={() => handleCustomerTypeChange('settler')}
                >
                    Settlers
                </button>
                <button
                    className={`${styles.tabButton} ${selectedCustomerType === 'debtor' ? styles.active : ''}`}
                    onClick={() => handleCustomerTypeChange('debtor')}
                >
                    Debtors
                </button>
            </div>

            {/* Loading State */}
            {loading && (
                <div className={styles.loadingMessage}>
                    <h3>Loading...</h3>
                    <p>Fetching {selectedCustomerType === 'settler' ? 'settled' : selectedCustomerType === 'debtor' ? 'debit' : 'all'} customers...</p>
                </div>
            )}

            {/* Error State */}
            {error && (
                <div className={styles.errorMessage}>
                    <h3>Error</h3>
                    <p>{error}</p>
                    <button onClick={() => fetchCustomersData(filterValues)} className={styles.retryButton}>
                        Retry
                    </button>
                </div>
            )}

            {/* Customers Grid */}
            {!loading && !error && filteredCustomers.length > 0 ? (
                <>
                    <div className={styles.customersGrid}>
                        {paginatedCustomers.map((customer) => (
                            <div
                                key={customer._id}
                                className={styles.customerCard}
                                onClick={(e) => handleViewCustomerProfile(customer, e)}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className={styles.customerInfo}>
                                    <div className={styles.customerImage}>
                                        <img
                                            src={customer.profile_picture || 'https://static.vecteezy.com/system/resources/previews/005/544/718/original/profile-icon-design-free-vector.jpg'}
                                            alt={customer.name}
                                            onError={(e) => {
                                                e.target.src = 'https://static.vecteezy.com/system/resources/previews/005/544/718/original/profile-icon-design-free-vector.jpg';
                                            }}
                                        />
                                    </div>
                                    <div className={styles.customerDetails}>
                                        <h3 className={styles.customerName}>{customer.name}</h3>
                                        {/* <p className={styles.customerAddress}>{customer.address}</p> */}
                                        <p className={styles.customerSwagId}>SWAG ID: {customer.swag_id}</p>
                                        {customer.unit_ids && customer.unit_ids.length > 0 && (
                                            <p className={styles.customerUnits}>
                                                Units: {customer.unit_ids.join(', ')}
                                            </p>
                                        )}
                                        {customer.contract_date && (<p className={styles.customerSwagId}>Contract date: {customer.contract_date}</p>)}

                                    </div>
                                    {/* <button
                                        className={styles.viewProfileButton}
                                        onClick={(e) => handleViewCustomerProfile(customer, e)}
                                        title="View Profile"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="12" cy="7" r="4"></circle>
                                        </svg>
                                    </button> */}
                                </div>

                                {(customer.installment_amount && customer.next_installment_date) && (
                                    <div className={styles.debtorInfo}>
                                        <div className={styles.remainingInstallments}>
                                            <span className={styles.label}>Next installment amount</span>
                                            <span className={styles.amount}>{formatCurrency(customer.installment_amount)}</span>
                                        </div>
                                        <div className={styles.deliveryDate}>
                                            <span className={styles.label}>Next installment date</span>
                                            <span className={styles.amount}>{formatDate(customer.next_installment_date)}</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Page Navigator - now uses server-side pagination */}
                    {pagination.last_page > 1 && (
                        <PageNavigator
                            currentPage={pagination.page}
                            totalPages={pagination.last_page}
                            onPageChange={handlePageChange}
                        />
                    )}
                </>
            ) : !loading && !error ? (
                <div className={styles.noCustomersMessage}>
                    <h3>No Customers Found</h3>
                    <p>There are currently no {selectedCustomerType === 'settler' ? 'settled' : selectedCustomerType === 'debtor' ? 'debit' : ''} customers to display.</p>
                </div>
            ) : null}

            {/* Filter Dialog */}
            <FilterDialog
                isOpen={showFilterDialog}
                onClose={handleFilterClose}
                onApply={handleFilterApply}
                title="Filter Customers"
                fields={filterFields}
                initialValues={filterValues}
            />
        </div>
    );
};

export default CustomersTabContent; 